import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { purchasesService } from '../../services/purchasesService';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function PurchasePayment({ isOpen, onClose, purchase, onPurchaseUpdated }) {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amountPaid: '',
    remainingBalance: ''
  });

  useEffect(() => {
    if (purchase) {
      setPaymentData({
        amountPaid: purchase.amountPaid || '',
        remainingBalance: (purchase.totalAmount - (purchase.amountPaid || 0)) || ''
      });
    }
  }, [purchase]);

  const handleNumericChange = (field, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPaymentData(prev => ({
        ...prev,
        [field]: value,
        remainingBalance: field === 'amountPaid' 
          ? (purchase.totalAmount - Number(value)).toFixed(2)
          : prev.remainingBalance
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updatedPurchase = await purchasesService.updatePurchasePayment(purchase.id, {
        ...paymentData,
        supplierId: purchase.supplierId,
        previousAmountPaid: purchase.amountPaid || 0
      });

      onPurchaseUpdated(updatedPurchase);
      onClose();
    } catch (error) {
      console.error('Error updating purchase payment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-lg w-full rounded-lg bg-white dark:bg-gray-800 p-6">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                Update Payment
              </Dialog.Title>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Amount
                </label>
                <input
                  type="text"
                  value={purchase?.totalAmount || 0}
                  disabled
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 
                    bg-gray-100 dark:bg-gray-700 px-3 py-2 text-gray-700 dark:text-gray-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Amount Paid
                </label>
                <input
                  type="text"
                  value={paymentData.amountPaid}
                  onChange={(e) => handleNumericChange('amountPaid', e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 
                    bg-white dark:bg-gray-700 px-3 py-2 text-gray-700 dark:text-gray-300 
                    focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Remaining Balance
                </label>
                <input
                  type="text"
                  value={paymentData.remainingBalance}
                  disabled
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 
                    bg-gray-100 dark:bg-gray-700 px-3 py-2 text-gray-700 dark:text-gray-300"
                />
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border 
                    border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 
                    focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border 
                    border-transparent rounded-md hover:bg-primary-700 focus:outline-none 
                    focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {loading ? 'Updating...' : 'Update Payment'}
                </button>
              </div>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
}